/**
 * Paste-to-chart import: plain text → ChordBaker song.
 *
 * Reads the two formats people paste: chords over lyrics (as chord sites
 * print them) and ChordPro. Pure and deterministic — no network, no LLM — so the
 * same text always gives the same chart. See
 * react-native/docs/features/paste-import.md for the heuristics and limits.
 */
import { getAllSlots, getRowSlotCount } from '../barUtils.js';
import { buildDraft, materializeSections } from './chartBuilder.js';
import { parseKeyText } from './keyText.js';
import { classifyLines, detectFormat } from './lineClassification.js';
import { normalizeLines } from './textNormalization.js';
const DEFAULT_LYRICS_MAX_CHARS = 60;
/**
 * Parses pasted chart text into a V4 song plus a summary for previewing it.
 *
 * Never throws: text with nothing recognizable gives a song with no sections.
 * Unrecognized tokens on chord lines become custom text, never dropped; lines
 * that aren't chart content (tablature, chord diagrams, strumming patterns,
 * metadata) are left out and counted in `skippedLines`.
 */
export const parseChartText = (text, options = {}) => {
    let nextId = 0;
    const materializeOptions = {
        lyricsMaxChars: options.lyricsMaxChars ?? DEFAULT_LYRICS_MAX_CHARS,
        generateId: options.generateId ?? ((kind) => `${kind}-paste-${++nextId}`),
    };
    const notes = { splitLyrics: 0, shortenedTexts: 0 };
    const lines = normalizeLines(text);
    const format = detectFormat(lines);
    const draft = buildDraft(classifyLines(lines, format));
    const sections = materializeSections(draft.sections, materializeOptions, notes);
    const slots = getAllSlots(sections);
    // Only a key the text states; a chart without one imports keyless
    const key = draft.keyText === null ? null : parseKeyText(draft.keyText);
    const keySource = key ? 'explicit' : 'none';
    return {
        song: {
            version: 4,
            title: draft.title.trim(),
            key,
            guitarShapes: {},
            sections,
            // Only what the text states; absent otherwise, like a song made in the app
            ...(draft.tempo !== null && { tempo: draft.tempo }),
            ...(draft.timeSignature !== null && { timeSignature: draft.timeSignature }),
        },
        format,
        keySource,
        sections: sections.map(section => ({
            title: section.title,
            repeat: section.repeat ?? null,
            rows: section.rows.length,
            slots: section.rows.reduce((sum, row) => sum + getRowSlotCount(row), 0),
        })),
        counts: {
            rows: sections.reduce((sum, section) => sum + section.rows.length, 0),
            slots: slots.length,
            chords: slots.filter(slot => slot.chord !== null).length,
            customTexts: slots.filter(slot => slot.customText !== null).length,
        },
        skippedLines: draft.skippedLines,
        splitLyrics: notes.splitLyrics,
        shortenedTexts: notes.shortenedTexts,
    };
};
